import React, { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAdminAuth } from "../AdminAuthContext";
import { ApiError } from "../api";
import { nvfoLogoWhite, nvfoLogoBlue } from "../../assets/imgExport";

// Split-screen layout ported from webridge's AdminLogin.tsx/AdminCMS.scss
// (dark gradient brand panel + grid-line texture on the left, plain white
// form card on the right, collapsing to form-only under 860px/md).
const gridTexture: React.CSSProperties = {
  backgroundImage:
    "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",
  backgroundSize: "42px 42px",
};

const brandGradient: React.CSSProperties = {
  background:
    "radial-gradient(120% 140% at 15% 10%, rgba(184,147,85,0.45) 0%, rgba(184,147,85,0) 55%), " +
    "radial-gradient(100% 120% at 90% 95%, rgba(184,147,85,0.28) 0%, rgba(184,147,85,0) 50%), " +
    "linear-gradient(160deg, #171e27 0%, #232d39 55%, #1b2530 100%)",
};

export default function AdminLoginPage() {
  const { currentAdmin, login } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation() as { state?: { from?: string } };
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (currentAdmin) {
    return <Navigate to={location.state?.from || "/admin/programs"} replace />;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      navigate(location.state?.from || "/admin/programs", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "登录失败，请重试");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen !flex !flex-col md:!grid grid-cols-1 md:grid-cols-[5fr_6fr] bg-white">
      <div
        className="hidden md:flex relative flex-col justify-between p-14 text-white overflow-hidden"
        style={brandGradient}
      >
        <div className="absolute inset-0 pointer-events-none" style={gridTexture} />
        <img
          src={nvfoLogoWhite}
          alt="NVFO"
          className="relative z-10 h-8 w-fit object-contain shrink-0"
        />
        <div className="relative z-10 max-w-md">
          <span className="inline-block text-xs font-semibold tracking-[0.14em] uppercase text-white/55 mb-4">
            NVFO Admin
          </span>
          <h2 className="text-3xl font-bold leading-snug mb-4 text-white">
            全球身份规划与家族办公室内容，一站式管理
          </h2>
          <p className="text-sm leading-relaxed text-white/65">
            在这里管理项目页面、文章与外部链接内容，统一维护官网展示的每一项信息。
          </p>
        </div>
        <div className="relative z-10 text-sm text-white/40">
          © {new Date().getFullYear()} New Vision Family Office
        </div>
      </div>

      <div className="flex items-center justify-center p-10">
        <form onSubmit={handleSubmit} className="w-full max-w-[380px]">
          <img
            src={nvfoLogoBlue}
            alt="NVFO"
            className="md:hidden h-7 w-fit object-contain shrink-0 mb-7"
          />
          <h1 className="text-2xl font-bold text-secondary-dark tracking-tight mb-1.5">欢迎回来</h1>
          <p className="text-secondary-light text-sm mb-8">请使用管理员账号登录</p>

          <label className="flex flex-col gap-1.5 text-sm font-semibold text-secondary-dark/80 mb-4">
            邮箱
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@newvgroup.com"
              className="px-3.5 py-3 border-[1.5px] border-fourth-light rounded-lg text-sm outline-none focus:border-gold focus:ring-4 focus:ring-gold/10 transition-all bg-white"
              autoComplete="username"
            />
          </label>

          <label className="flex flex-col gap-1.5 text-sm font-semibold text-secondary-dark/80 mb-5">
            密码
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="请输入密码"
              className="px-3.5 py-3 border-[1.5px] border-fourth-light rounded-lg text-sm outline-none focus:border-gold focus:ring-4 focus:ring-gold/10 transition-all bg-white"
              autoComplete="current-password"
            />
          </label>

          {error && (
            <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3.5 py-2.5 mb-5">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-gold hover:bg-gold-light disabled:opacity-55 text-white font-bold rounded-lg py-3.5 text-sm shadow-[0_4px_14px_rgba(184,147,85,0.35)] hover:shadow-[0_8px_20px_rgba(184,147,85,0.4)] hover:-translate-y-px active:translate-y-0 transition-all"
          >
            {submitting ? "登录中…" : "登录"}
          </button>
        </form>
      </div>
    </div>
  );
}
